/**
 * Общие лимиты и цены приложения.
 *
 * Константы вынесены сюда, потому что файлы с директивой "use server" не могут
 * экспортировать значения, отличные от async функций.
 *
 * RULE-CONTENT-06: при изменении цены здесь — обновить ВСЕ места где цена
 * упоминается текстом:
 *   - src/app/(public)/pricing/page.tsx (UI + meta + FAQ schema)
 *   - src/app/(public)/faq/page.tsx (FAQ schema)
 *   - src/app/(public)/about/page.tsx
 *   - src/app/(public)/profile-preview/page.tsx
 *   - src/app/(dashboard)/settings/subscription/page.tsx
 *   - src/components/sections/PricingPreviewSection.tsx
 *   - src/components/dashboard/PlusUpsellBanner.tsx
 *   - src/lib/email.ts (welcome, day3, day7, person-birthday templates)
 *   - public/llms.txt и src/app/llms-full.txt/route.ts
 */

// ─── Лимиты ─────────────────────────────────────────────────────────────────

/** Лимит на смену даты рождения в /settings.
 * Защита от подмены данных юзером Plus (поменял дату → получил расчёты
 * другого человека → подмена обратно). Первое заполнение не считается.
 * При исчерпании поле блокируется в UI и сервер отклоняет смену.
 * 2026-06-01: снижено с 2 до 1 — для «узнать про другого человека» есть
 * /people (бесплатно до 3, Plus до 20). Дата рождения — это твой ключ,
 * её не должно быть «удобно» менять. */
export const BIRTH_DATE_LIMIT = 1;

/** Лимиты на «Мои люди» (Person) по тарифу.
 * 2026-06-01: Free снижен с 3 до 2. Близкий круг у людей маленький (2–4),
 * 3 закрывало интерес и убирало триггер на Plus. 2 = партнёр + 1 другой =
 * чувство «работает», 3-й слот = триггер апгрейда на конкретного человека. */
export const PEOPLE_LIMITS = {
  free: 2,
  plus: 20,
} as const;

// ─── Цены подписки Plus ─────────────────────────────────────────────────────

/**
 * Цены подписки Plus в ЕВРО (EUR).
 *
 * ⚠️ taliora-west: значения — PLACEHOLDER (как бренд/домен). Это бизнес-решение
 * по ценообразованию для западного рынка — ПОДТВЕРДИТЬ/ИЗМЕНИТЬ перед запуском.
 * Меняются ТОЛЬКО здесь → pricing.ts пересчитывает все строки, скидки и JSON-LD
 * автоматически (Intl es-ES → «5,99 €»). Скидки квартала/полугода ВЫВОДЯТСЯ из
 * чисел (см. pricing.ts), не хардкодятся.
 */
export const PLUS_PRICES = {
  /** Descuento de primer mes DESACTIVADO (2026-07): la entrada es el precio
   *  mensual normal; el gancho de conversión es el plan ANUAL (héroe, −58%).
   *  Se mantiene la clave `firstMonth` (= monthly) para no romper consumidores;
   *  al no haber diferencia, no se muestra ninguna oferta de «primer mes». */
  firstMonth: 7.99,
  /** Mensual — ANCLA: precio alto por mes para que los planes largos parezcan
   *  regalados (decoy). Aun así por debajo de CHANI ($11.99) y Nebula ($24.99). */
  monthly: 7.99,
  /** 3 meses — mantenido por compatibilidad; NO se destaca en la UI (evita
   *  parálisis de elección; la escalera visible es Mensual/Semestral/Anual). */
  quarter: 14.99,
  /** Semestral — escalón intermedio (≈$4.67/mes, −42%). */
  halfYear: 27.99,
  /** Anual — HÉROE de la conversión: ≈$3.33/mes, −58%, «$0.11/día». Máximo LTV
   *  y mejor ancla. Todos los competidores lideran con el plan anual. */
  year: 39.99,
} as const;

/**
 * Цена «разового разбора» — оставлена как константа для legacy кода
 * (report-prices.ts использует для отображения «бывшей» цены).
 * Фича разовой покупки удалена 2026-05-28 — только подписка Plus.
 */
export const SINGLE_REPORT_PRICE = 1.49; // legacy, не используется в UI (placeholder EUR)

/**
 * Цена расширенного (премиум) уровня разбора в ЕВРО (EUR).
 * Монетизация v2: 3 разбора (Деньги/Натальная/Детский) — двухуровневая модель:
 * базовый уровень входит в Plus, расширенный покупается отдельно навсегда.
 *
 * ⚠️ taliora-west: PLACEHOLDER EUR. Якорь PREMIUM_REPORT_ANCHOR — зачёркнутая
 * цена для маркетинга (~50% скидка). Подтвердить перед запуском.
 */
export const PREMIUM_REPORT_PRICE = 19.99;
/** Зачёркнутый якорь для премиум-разбора (маркетинг, placeholder EUR). */
export const PREMIUM_REPORT_ANCHOR = 39.99;

/**
 * Фиче-флаг продажи расширенного уровня премиум-разборов.
 * false → механика заложена, но кнопки покупки/блоки-приманки СКРЫТЫ; на сайте
 *         всё работает как раньше (Plus видит разбор, free — PlusGate). Безопасно
 *         для деплоя пока расширенный контент ещё не дописан (Фаза 2).
 * true  → расширенные блоки + кнопка «Открыть за $19.99» становятся видимы.
 *
 * ВКЛЮЧАТЬ только когда расширенный контент готов (docs/monetization-v2.md, Фаза 2).
 */
export const EXTENDED_REPORTS_ENABLED = true;

/**
 * LAUNCH_MODE — «acceso anticipado»: mientras no haya facturación (sin cuenta de
 * pagos ni entidad legal), Plus es GRATIS para todo usuario registrado.
 *
 * Efectos (env `NEXT_PUBLIC_LAUNCH_MODE=true`):
 *   - `isPremiumUser()` devuelve true para cualquier usuario logueado → sin paywalls.
 *   - /pricing y CheckoutButton muestran «Gratis en acceso anticipado · regístrate»
 *     en vez de precios/checkout (no hay pasarela conectada aún).
 *
 * ⚠️ OPS: `NEXT_PUBLIC_*` se inserta en el BUILD → cambiar el flag exige un
 * `next build` completo (`bash deploy.sh` lo hace); un simple restart de PM2 NO
 * basta. Al desactivar el lanzamiento: primero ejecutar `/api/cron/founder-grant`,
 * luego poner el flag a false y re-desplegar (ver payments/README).
 *
 * Al abrir la entidad + conectar Stripe: poner `NEXT_PUBLIC_LAUNCH_MODE=false`
 * (o quitarlo), luego dar a los usuarios tempranos un «periodo de fundador» +
 * descuento (grant único). Reversible con un solo flag, sin escribir en la BD.
 */
export const LAUNCH_MODE = process.env.NEXT_PUBLIC_LAUNCH_MODE === "true";

/** Сумма для автопродления (cron) — соответствует тарифу пользователя. */
export const RENEWAL_AMOUNTS: Record<"monthly" | "quarter" | "halfyear" | "year", number> = {
  monthly: PLUS_PRICES.monthly,
  quarter: PLUS_PRICES.quarter,
  halfyear: PLUS_PRICES.halfYear,
  year: PLUS_PRICES.year,
};

/** Реферальный бонус — N дней Plus при регистрации по ссылке (старая модель, off). */
export const REFERRAL_BONUS_DAYS = 30;

/**
 * Парная синхронность Tier 2 Заход 3 (2026-06-13): награда «+N дней Plus ОБОИМ»
 * — приглашённому и пригласившему — когда ПРИГЛАШЁННЫЙ КУПИЛ Plus (RULE-UX-18).
 * Триггер — покупка (не регистрация) → фрод-безопасно: чтобы накрутить, нужно
 * реально оплатить Plus. Источник истины суммы дней.
 */
export const INVITE_REWARD_DAYS = 7;
/** Лимит наград приглашающему в МСК-месяц (страховка поверх платёж-гейта). */
export const INVITE_REWARD_INVITER_CAP_PER_MONTH = 5;

/**
 * Streak Freeze (RULE-STREAK-FREEZE): сколько «заморозок» серии получает Plus
 * в начале каждого МСК-месяца. Free — 0 (серия сгорает при пропуске → стимул к Plus).
 */
export const PLUS_STREAK_FREEZES_PER_MONTH = 2;

/**
 * Feature flag — реферальная программа.
 * Source of truth — `src/lib/referral.ts:REFERRAL_PROGRAM_ENABLED` (server-side).
 * Эта константа — дубль для client components (page register показывает баннер).
 * При смене значения — менять в ОБОИХ местах одновременно.
 *
 * Когда false:
 *  - applyReferralBonus() — no-op (бонус не применяется)
 *  - Реф-баннер на /auth/register — скрыт
 *  - Кнопки «Поделиться реф-ссылкой» — скрыты
 */
export const REFERRAL_PROGRAM_ENABLED = false;

/**
 * AI cost cap — порог числа кандидатов для weekly-digest cron (RULE-AI-COST-01).
 * При неожиданном росте Plus юзеров до этого числа за неделю — отключаем AI-вступление
 * (fallback на шаблон) чтобы не получить runaway-расход YandexGPT.
 *
 * Реальная стоимость: 0.32₽/вызов yandexgpt-lite × N candidates ≈ N × 0.32₽/нед.
 * При cap=500 максимум ~640₽/мес — управляемый риск.
 *
 * Пересмотреть когда подключим budget-cap cron с email-alert на hola@zaruvia.com.
 */
export const WEEKLY_AI_INTRO_USER_CAP = 500;
